import { useEffect, useState, useMemo } from 'react'
import { useForm, useFieldArray, type FieldErrors } from 'react-hook-form'
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import { Info, Trash2, X as XIcon, Save, Plus, Pencil } from 'lucide-react'
import { Input } from '@components/Input/Input'
import { Textarea } from '@components/Textarea/Textarea'
import { Button } from '@components/Button/Button'
import { Badge } from '@components/Badge/Badge'
import { LookupField } from '@components/LookupField'
import { Table } from '@components/Table/Table'
import type { TableColumn } from '@components/Table/Table.types'
import { Select } from '@components/Select/Select'
import { toast } from '@components/Toast'
import { createContactSchema } from '../schemas/contact.schema'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { PillEmailInput } from './PillEmailInput'
import { UseForDropdown } from './UseForDropdown'
import { DefaultConfirmationDialog } from './DefaultConfirmationDialog'
import { UnifiedPhoneInput } from './UnifiedPhoneInput'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { cn } from '../utils/cn'
import { useTranslations } from '../providers/ContactServiceProvider'
import type { Contact, ContactSocialNetwork } from '../types/contact.types'
import type { LookupConfig } from '../types/adapter.types'
import type { EnumOption } from '../types/enum.types'
import { DEFAULT_VALUE } from '../types/enum.types'
import type { z } from 'zod'

export type ContactModalMode = 'create' | 'edit' | 'view'

export interface ContactModalProps {
  open: boolean
  mode: ContactModalMode
  contact: Contact | null
  lookupConfig: LookupConfig
  useForOptions?: EnumOption[]
  phoneCategoryOptions?: EnumOption[]
  isFirstContact?: boolean
  currentDefaultContactName?: string
  isOnlyDefault?: boolean
  currentDefaultOnlyHasDefault?: boolean
  onSave: (data: ContactModalFormData) => void
  onClose: () => void
}

const DEFAULT_SOCIAL_NETWORK: ContactSocialNetwork = {
  socialNetworkTypeId: '',
  url: '',
}

const TAB_BASIC_DATA = 'basic-data'
const TAB_LOCATION = 'location'
const TAB_PHONE = 'phone'
const TAB_SOCIAL = 'social-network'

/** Maps a form field name to the tab that contains it */
function getTabForField(fieldName: string): string {
  if (fieldName.startsWith('phones')) return TAB_PHONE
  if (fieldName.startsWith('socialNetworks')) return TAB_SOCIAL
  if (fieldName === 'cityId' || fieldName === 'neighborhoodId' || fieldName === 'address') return TAB_LOCATION
  return TAB_BASIC_DATA
}

export type ContactModalFormData = z.infer<ReturnType<typeof createContactSchema>>

export function ContactModal({
  open,
  mode,
  contact,
  lookupConfig,
  useForOptions,
  phoneCategoryOptions,
  isFirstContact,
  currentDefaultContactName,
  isOnlyDefault,
  currentDefaultOnlyHasDefault,
  onSave,
  onClose,
}: ContactModalProps) {
  const isMobile = useMediaQuery('(max-width: 767px)')
  const isViewMode = mode === 'view'
  const t = useTranslations()

  const [activeTab, setActiveTab] = useState(TAB_BASIC_DATA)

  const requireClassification = mode === 'edit' || isFirstContact === false
  const modalFormSchema = useMemo(() => {
    const full = createContactSchema(t.schema, { requireClassification })
    return full.pick({
      name: true,
      emails: true,
      address: true,
      description: true,
      cityId: true,
      neighborhoodId: true,
      useFor: true,
      phones: true,
      socialNetworks: true,
    })
  }, [t.schema, requireClassification])

  const categories = phoneCategoryOptions && phoneCategoryOptions.length > 0
    ? phoneCategoryOptions
    : [{ value: 0, label: t.enums.phoneCategory[0] ?? t.modal.defaultCategory }] as EnumOption[]

  const getCategoryLabel = (categoryValue: number): string => {
    return categories.find((c) => c.value === categoryValue)?.label ?? String(categoryValue)
  }

  // LookupField capability detection (presence of config enables the section)
  const hasCities = !!lookupConfig.cities
  const hasNeighborhoods = !!lookupConfig.neighborhoods
  const hasAnyGeoConfig = hasCities || hasNeighborhoods

  // Default transfer confirmation dialog state
  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [pendingUseFor, setPendingUseFor] = useState<number[] | null>(null)
  const [preventRemovalError, setPreventRemovalError] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    setFocus,
    trigger,
    control,
    formState: { errors },
  } = useForm<ContactModalFormData>({
    resolver: standardSchemaResolver(modalFormSchema),
    defaultValues: {
      name: '',
      emails: [],
      address: null,
      description: null,
      cityId: null,
      neighborhoodId: null,
      useFor: [],
      phones: [],
      socialNetworks: [],
    },
  })

  const { fields: phoneFields, prepend: prependPhone, remove: removePhone } = useFieldArray({
    control,
    name: 'phones',
  })

  const { fields: socialNetworkFields, prepend: prependSocialNetwork, remove: removeSocialNetwork } = useFieldArray({
    control,
    name: 'socialNetworks',
  })

  useEffect(() => {
    if (open) {
      setPreventRemovalError(false)
      setTransferDialogOpen(false)
      setPendingUseFor(null)
      setActiveTab(TAB_BASIC_DATA)
      if (mode === 'create') {
        reset({ name: '', emails: [], address: null, description: null, cityId: null, neighborhoodId: null, useFor: [], phones: [], socialNetworks: [] })
      } else if (contact) {
        reset({
          name: contact.name,
          emails: contact.emails,
          address: contact.address,
          description: contact.description,
          cityId: contact.cityId,
          neighborhoodId: contact.neighborhoodId,
          useFor: contact.useFor,
          phones: contact.phones.length > 0 ? contact.phones : [],
          socialNetworks: contact.socialNetworks.length > 0 ? contact.socialNetworks : [],
        })
      }
    }
  }, [open, mode, contact, reset])

  const emails = watch('emails')
  const nameValue = watch('name')

  const handleCityChange = (record: Record<string, unknown> | null) => {
    const id = record ? String(record['Id'] ?? record['id'] ?? '') : null
    setValue('cityId', id)
    setValue('neighborhoodId', null)
  }

  const handleNeighborhoodChange = (record: Record<string, unknown> | null) => {
    const id = record ? String(record['Id'] ?? record['id'] ?? '') : null
    setValue('neighborhoodId', id)
  }

  const onSubmit = (data: ContactModalFormData) => {
    onSave(data)
  }

  const onError = (formErrors: FieldErrors<ContactModalFormData>) => {
    // Cross-tab validation: navigate to the tab with the first error
    const firstErrorField = Object.keys(formErrors)[0] as keyof ContactModalFormData | undefined
    if (firstErrorField) {
      const targetTab = getTabForField(firstErrorField)
      setActiveTab(targetTab)
      if (firstErrorField !== 'emails' && firstErrorField !== 'phones' && firstErrorField !== 'socialNetworks') {
        setTimeout(() => setFocus(firstErrorField), 0)
      }
    }
  }

  const handleAddPhone = async () => {
    const phones = watch('phones')
    if (phones.length > 0) {
      const valid = await trigger('phones.0')
      if (!valid) return
    }
    prependPhone({
      category: categories[0].value,
      countryId: '',
      areaCode: '',
      number: '',
      extension: '',
    })
    setTimeout(() => setFocus('phones.0.number'), 0)
  }

  /** Returns true if dropdown should stay open, false if it should close */
  const handleUseForChange = (selected: number[]): boolean => {
    const currentUseFor = watch('useFor')
    const wasAdded = selected.includes(DEFAULT_VALUE) && !currentUseFor.includes(DEFAULT_VALUE)
    const wasRemoved = !selected.includes(DEFAULT_VALUE) && currentUseFor.includes(DEFAULT_VALUE)

    // Clear stale errors on every call
    setPreventRemovalError(false)

    if (wasAdded && currentDefaultContactName) {
      if (currentDefaultOnlyHasDefault) {
        // Block — old default would be left with no classification. A toast (not an inline
        // field message) because this is a rejected click, not a persistent validation state —
        // the dropdown selection itself doesn't change, so there's nothing left under the field
        // to annotate.
        toast.error(t.modal.error.transferBlocked)
        return false
      }
      // Transfer needed — show confirmation dialog, close dropdown
      setPendingUseFor(selected)
      setTransferDialogOpen(true)
      return false
    }

    if (wasRemoved && isOnlyDefault) {
      // Cannot remove only default — show error
      setPreventRemovalError(true)
      return true
    }

    setValue('useFor', selected)
    return true
  }

  const handleTransferConfirm = () => {
    if (pendingUseFor) {
      setValue('useFor', pendingUseFor)
    }
    setPendingUseFor(null)
    setTransferDialogOpen(false)
  }

  const handleTransferCancel = () => {
    setPendingUseFor(null)
    setTransferDialogOpen(false)
  }

  const tabItems = [
    { id: TAB_BASIC_DATA, label: t.modal.tab.basicData },
    { id: TAB_LOCATION, label: t.modal.tab.location },
    { id: TAB_PHONE, label: t.modal.tab.phone },
    { id: TAB_SOCIAL, label: t.modal.tab.socialNetwork },
  ]

  const handleAddSocialNetwork = async () => {
    const socialNetworks = watch('socialNetworks')
    if (socialNetworks.length > 0) {
      const valid = await trigger('socialNetworks.0')
      if (!valid) return
    }
    prependSocialNetwork({ ...DEFAULT_SOCIAL_NETWORK })
    setTimeout(() => setFocus('socialNetworks.0.url'), 0)
  }

  const phoneTableColumns: TableColumn<Record<string, unknown>>[] = [
    {
      header: t.modal.label.category,
      accessor: 'category',
      width: '130px',
      render: (_value, _row, index) =>
        isViewMode ? (
          <span className="text-sm text-content-primary dark:text-dark-content-primary">
            {getCategoryLabel(Number((contact?.phones ?? [])[index]?.category ?? 0))}
          </span>
        ) : (
          <Select
            options={categories.map((c) => ({ value: c.value, label: c.label }))}
            value={watch(`phones.${index}.category`)}
            onChange={(val) => setValue(`phones.${index}.category`, Number(val))}
            ariaLabel={t.modal.aria.phoneCategory(index)}
          />
        ),
    },
    {
      header: t.modal.label.country,
      accessor: 'countryId',
      // Wide enough that the LookupField trigger and its floating dropdown share the
      // same width — narrower than this and the dropdown (driven by its own search box
      // + operator toggle content) grows past the trigger and visually spills into the
      // "Teléfono" column next to it. fixedWidth: without it, Table treats `width` as a
      // MINIMUM (minmax(260px, 1fr)) and still lets the column grow/shrink with content
      // (e.g. narrower once cleared) — fixedWidth pins it at exactly 260px always.
      width: '260px',
      fixedWidth: true,
      render: (_value, _row, index) => {
        const phoneCountryId = isViewMode
          ? ((contact?.phones ?? [])[index]?.countryId || null)
          : (watch(`phones.${index}.countryId`) || null)
        return (
          // w-full: this cell's wrapper is a flex container (Table.tsx), so without an
          // explicit width this div — and LookupField inside it — shrinks to fit its own
          // content (e.g. narrower for "Select..." than for "57 - Colombia") instead of
          // filling the fixedWidth column. That's what made the field visibly grow/shrink.
          <div className="w-full">
            <LookupField
              key={phoneFields[index]?.id}
              entity={lookupConfig.countries.entity}
              label=""
              // Empty label still occupies a row-gap slot in LookupField's own flex-col
              // layout even with zero height, pushing the trigger a few px below sibling
              // controls (input, buttons) in the same table row — this class removes it
              // from layout entirely instead of just hiding its (empty) text.
              className="lookup-field--label-hidden"
              displayFields={lookupConfig.countries.displayFields}
              displayTemplate={lookupConfig.countries.displayTemplate}
              value={phoneCountryId}
              onChange={isViewMode ? () => {} : (record) => {
                const id = record ? String(record['Id'] ?? record['id'] ?? '') : ''
                setValue(`phones.${index}.countryId`, id)
              }}
              fetcher={lookupConfig.fetcher}
              readOnly={isViewMode}
              disabled={isViewMode}
              error={!isViewMode && !!errors.phones?.[index]?.countryId}
              errorText={errors.phones?.[index]?.countryId?.message}
              onCreateNew={lookupConfig.countries.onCreateNew}
              createButtonLabel={lookupConfig.countries.createButtonLabel}
              createTitle={lookupConfig.countries.createTitle}
              createShowHeader={lookupConfig.countries.createShowHeader}
              createWidth={lookupConfig.countries.createWidth}
              createHeight={lookupConfig.countries.createHeight}
            />
          </div>
        )
      },
    },
    {
      header: t.modal.tab.phone,
      accessor: 'number',
      render: (_value, _row, index) => {
        const phoneData = isViewMode
          ? (contact?.phones ?? [])[index]
          : undefined
        return (
          <div>
            <UnifiedPhoneInput
              value={isViewMode
                ? { areaCode: phoneData?.areaCode ?? '', number: phoneData?.number ?? '', extension: phoneData?.extension ?? '' }
                : {
                    areaCode: watch(`phones.${index}.areaCode`) ?? '',
                    number: watch(`phones.${index}.number`) ?? '',
                    extension: watch(`phones.${index}.extension`) ?? '',
                  }
              }
              onChange={isViewMode ? () => {} : (phoneVal) => {
                setValue(`phones.${index}.areaCode`, phoneVal.areaCode)
                setValue(`phones.${index}.number`, phoneVal.number)
                setValue(`phones.${index}.extension`, phoneVal.extension)
              }}
              disabled={isViewMode}
              hasError={!isViewMode && (!!errors.phones?.[index]?.number || !!errors.phones?.[index]?.areaCode || !!errors.phones?.[index]?.extension)}
            />
            {!isViewMode && errors.phones?.[index]?.number && (
              <p id={`phone-${index}-number-error`} className="mt-1 text-xs text-red-600" role="alert">
                {errors.phones[index].number.message}
              </p>
            )}
            {!isViewMode && errors.phones?.[index]?.areaCode && (
              <p id={`phone-${index}-area-error`} className="mt-1 text-xs text-red-600" role="alert">
                {errors.phones[index].areaCode.message}
              </p>
            )}
            {!isViewMode && errors.phones?.[index]?.extension && (
              <p id={`phone-${index}-ext-error`} className="mt-1 text-xs text-red-600" role="alert">
                {errors.phones[index].extension.message}
              </p>
            )}
          </div>
        )
      },
    },
    ...(!isViewMode ? [{
      header: t.modal.column.actions,
      accessor: 'id' as const,
      // Narrower than the other columns' widths — this one only ever holds a single icon-only
      // delete button, not free-form content, so it doesn't need their room. Still wide enough
      // for the "Acciones" header text itself to render in full (56px clipped it to "A...").
      // fixedWidth (unlike Table's minmax(width, 1fr) default) pins it here instead of
      // stretching into any leftover row space.
      width: '100px',
      fixedWidth: true,
      stickyRight: true,
      render: (_value: unknown, _row: Record<string, unknown>, index: number) => (
        <Button
          type="plain"
          size="xs"
          iconOnly
          leftIcon={<Trash2 className="h-4 w-4 text-content-extensions-red" />}
          onClick={() => removePhone(index)}
          ariaLabel={t.modal.aria.removePhone(index)}
        />
      ),
    }] as TableColumn<Record<string, unknown>>[] : []),
  ]

  const socialTableColumns: TableColumn<Record<string, unknown>>[] = [
    {
      header: t.modal.label.socialType,
      accessor: 'socialNetworkTypeId',
      // Same reasoning as the phone table's "País" column — wide enough that the
      // dropdown doesn't grow past its own trigger and spill into the "URL" column,
      // and pinned with fixedWidth so it doesn't grow/shrink with content either.
      width: '260px',
      fixedWidth: true,
      render: (_value, _row, index) => {
        const snTypeId = isViewMode
          ? ((contact?.socialNetworks ?? [])[index]?.socialNetworkTypeId || null)
          : (watch(`socialNetworks.${index}.socialNetworkTypeId`) || null)
        return (
          // w-full — same reasoning as the phone table's "País" cell above.
          <div className="w-full">
            <LookupField
              key={socialNetworkFields[index]?.id}
              entity={lookupConfig.socialNetworkTypes.entity}
              label=""
              // Empty label still occupies a row-gap slot in LookupField's own flex-col
              // layout even with zero height, pushing the trigger a few px below sibling
              // controls (input, buttons) in the same table row — this class removes it
              // from layout entirely instead of just hiding its (empty) text.
              className="lookup-field--label-hidden"
              displayFields={lookupConfig.socialNetworkTypes.displayFields}
              value={snTypeId}
              onChange={isViewMode ? () => {} : (record) => {
                const id = record ? String(record['Id'] ?? record['id'] ?? '') : ''
                setValue(`socialNetworks.${index}.socialNetworkTypeId`, id)
              }}
              fetcher={lookupConfig.fetcher}
              readOnly={isViewMode}
              disabled={isViewMode}
              error={!isViewMode && !!errors.socialNetworks?.[index]?.socialNetworkTypeId}
              errorText={errors.socialNetworks?.[index]?.socialNetworkTypeId?.message}
              onCreateNew={lookupConfig.socialNetworkTypes.onCreateNew}
              createButtonLabel={lookupConfig.socialNetworkTypes.createButtonLabel}
              createTitle={lookupConfig.socialNetworkTypes.createTitle}
              createShowHeader={lookupConfig.socialNetworkTypes.createShowHeader}
              createWidth={lookupConfig.socialNetworkTypes.createWidth}
              createHeight={lookupConfig.socialNetworkTypes.createHeight}
            />
          </div>
        )
      },
    },
    {
      header: t.modal.label.socialUrl,
      accessor: 'url',
      // No width before this ('1fr' default) let the column shrink enough to truncate
      // the URL text. A sane minimum keeps it readable without a horizontal scroll.
      width: '320px',
      render: (_value, _row, index) => {
        if (isViewMode) {
          const sn = (contact?.socialNetworks ?? [])[index]
          return (
            <span className="text-sm text-content-primary dark:text-dark-content-primary truncate">
              {sn?.url || '—'}
            </span>
          )
        }
        return (
          // w-full — same reasoning as the "País"/"Red social" lookup cells above: this
          // cell's wrapper is a flex container (Table.tsx), so without an explicit width
          // this div shrinks to fit its own content instead of filling the column.
          <div className="w-full">
            <Input
              {...(register(`socialNetworks.${index}.url`) as any)}
              type="url"
              maxLength={2048}
              placeholder={t.modal.placeholder.socialUrl}
              className={errors.socialNetworks?.[index]?.url ? 'border-red-500' : ''}
              aria-label={t.modal.aria.socialUrl(index)}
              aria-invalid={!!errors.socialNetworks?.[index]?.url}
              aria-describedby={errors.socialNetworks?.[index]?.url ? `sn-${index}-url-error` : undefined}
              fullWidth
            />
            {errors.socialNetworks?.[index]?.url && (
              <p id={`sn-${index}-url-error`} className="mt-1 text-xs text-red-600" role="alert">
                {errors.socialNetworks[index].url.message}
              </p>
            )}
          </div>
        )
      },
    },
    ...(!isViewMode ? [{
      header: t.modal.column.actions,
      accessor: 'id' as const,
      // 100px (not 56px) — narrow enough that it only ever fits the single icon-only delete
      // button, but wide enough for the "Acciones" header text to render in full.
      width: '100px',
      fixedWidth: true,
      stickyRight: true,
      render: (_value: unknown, _row: Record<string, unknown>, index: number) => (
        <Button
          type="plain"
          size="xs"
          iconOnly
          leftIcon={<Trash2 className="h-4 w-4 text-content-extensions-red" />}
          onClick={() => removeSocialNetwork(index)}
          ariaLabel={t.modal.aria.removeSocial(index)}
        />
      ),
    }] as TableColumn<Record<string, unknown>>[] : []),
  ]

  return (
    <Dialog modal={false} open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose() }}>
      <DialogContent
        showCloseButton={false}
        className={cn(
          'flex flex-col overflow-hidden',
          isMobile && 'w-full h-full max-w-full rounded-none translate-x-0 translate-y-0 top-0 left-0',
          !isMobile && 'sm:max-w-[1000px] max-h-[90vh]',
        )}
        onOpenAutoFocus={(e) => {
          if (!isViewMode) {
            e.preventDefault()
            setTimeout(() => setFocus('name'), 0)
          }
        }}
      >
        <form onSubmit={handleSubmit(onSubmit, onError)} noValidate className="flex flex-col flex-1 min-h-0">
          {/* Title on the left, Cancelar/Guardar (or just Cerrar in view mode) top-right — no
              separate "X" close button anymore (showCloseButton={false} above), Cancelar/Cerrar
              already close the modal. Matches BranchDetailModal's header (BranchPanel.tsx). */}
          <DialogHeader className="shrink-0 pb-4 flex-row items-center justify-between gap-4 space-y-0">
            <div className="flex flex-col gap-2">
              {/* Same blue as BranchDetailModal's title (BranchPanel.tsx, text-[#1355b7]),
                  traced live from the main Tercero form. Live-updates from the "Nombre" field
                  while typing, same as the contact's own name would once saved. */}
              <DialogTitle className="text-xl font-semibold text-[#1355b7] tracking-tight leading-7">
                {t.modal.titlePrefix}{nameValue ? `: ${nameValue}` : ''}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {t.modal.description}
              </DialogDescription>
              {!isViewMode && (
                <Badge
                  label={mode === 'create' ? t.modal.actionBadge.creating : t.modal.actionBadge.editing}
                  color="yellow"
                  leftIcon={<Pencil size={12} />}
                />
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {isViewMode ? (
                <Button type="outline" htmlType="button" leftIcon={<XIcon size={16} />} onClick={onClose}>
                  {t.modal.button.close}
                </Button>
              ) : (
                <>
                  <Button type="outline" htmlType="button" leftIcon={<XIcon size={16} />} onClick={onClose}>
                    {t.modal.button.cancel}
                  </Button>
                  <Button type="default" htmlType="submit" leftIcon={<Save size={16} />}>
                    {mode === 'create' ? t.modal.button.save : t.modal.button.update}
                  </Button>
                </>
              )}
            </div>
          </DialogHeader>

          {/* Tabs + content share one bordered/rounded frame — matches BranchDetailModal's
              card exactly (BranchPanel.tsx, traced live from MasterPatternView's own TabBar). */}
          <div className="flex-1 flex flex-col min-h-0 rounded-xl border border-neutral-200 dark:border-dark-border-primary overflow-hidden">
            <div role="tablist" aria-label={t.modal.description} className="flex flex-shrink-0 gap-1 px-3 pt-2">
              {tabItems.map((tab) => {
                const isTabActive = tab.id === activeTab
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isTabActive}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      'relative flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold whitespace-nowrap cursor-pointer rounded-t-lg bg-[#f8f9fc] transition-colors',
                      isTabActive ? 'text-blue-600' : 'text-zinc-500 hover:text-zinc-700 hover:bg-zinc-100',
                    )}
                  >
                    {tab.label}
                    {isTabActive && (
                      <div className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-blue-600" />
                    )}
                  </button>
                )
              })}
            </div>

          <div className="flex-1 flex flex-col min-h-0 p-4">
            {/* Tab: Basic Data */}
            <div className={activeTab === TAB_BASIC_DATA ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
              <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
                {/* UseFor + Nombre on same row — but only when "Usar para" actually renders;
                    otherwise Nombre must fill the full row instead of sitting in a lone,
                    half-width grid cell next to nothing. */}
                <div className={cn('grid grid-cols-1 gap-4', useForOptions && useForOptions.length > 1 && 'sm:grid-cols-2')}>
                  {/* Usar para */}
                  {useForOptions && useForOptions.length > 1 && (
                    <div className="space-y-3">
                      {/* Nombre's label comes from Input's own labelVariants (text-base,
                          8px gap) — this custom label is text-sm (4px shorter) with its
                          own gap, so space-y-3 (vs. the visually-matched-looking 1.5)
                          makes up the difference and keeps both controls' top edge level. */}
                      <label className="block text-sm font-bold text-content-primary dark:text-dark-content-primary">
                        {t.modal.label.useFor} {requireClassification && <span className="text-destructive ml-1">*</span>}
                      </label>
                      <UseForDropdown
                        value={watch('useFor')}
                        options={useForOptions}
                        onChange={handleUseForChange}
                        disabled={isViewMode}
                      />
                      {/* transferBlockedError (blocked double-"Predeterminado") no longer renders
                          inline here — it's a one-off, transient notice about a click that was
                          just rejected, not a persistent field validation state, so it now fires
                          as a toast (see handleUseForChange) instead of competing for this same
                          line with preventRemoval/schema errors. */}
                      {preventRemovalError && (
                        <p className="text-xs text-red-600" role="alert">
                          {t.modal.error.preventRemoval}
                        </p>
                      )}
                      {errors.useFor && !preventRemovalError && (
                        <p className="text-xs text-red-600" role="alert">
                          {errors.useFor.message}
                        </p>
                      )}
                    </div>
                  )}
                  {/* Nombre */}
                  <div>
                    {isViewMode ? (
                      <Input
                        label={t.modal.label.name}
                        value={contact?.name || '—'}
                        disabled
                        readOnly
                        onChange={() => {}}
                      />
                    ) : (
                      <Input
                        label={t.modal.label.name}
                        required
                        placeholder={t.modal.placeholder.name}
                        {...(register('name') as any)}
                        error={!!errors.name}
                        errorMessage={errors.name?.message}
                        aria-invalid={!!errors.name}
                        fullWidth
                      />
                    )}
                  </div>
                </div>
                {errors.useFor && !(useForOptions && useForOptions.length > 1) && (
                  <p className="text-xs text-red-600" role="alert">
                    {errors.useFor.message}
                  </p>
                )}

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-content-primary dark:text-dark-content-primary">
                    {t.modal.label.email}
                  </label>
                  <PillEmailInput
                    id="contact-emails"
                    value={emails}
                    onChange={(newEmails) => setValue('emails', newEmails, { shouldValidate: false })}
                    disabled={isViewMode}
                    hasError={!!errors.emails}
                    aria-invalid={!!errors.emails}
                    aria-describedby={errors.emails ? 'contact-emails-error' : undefined}
                  />
                  {errors.emails && (
                    <p id="contact-emails-error" className="text-xs text-red-600" role="alert">
                      {errors.emails.message || errors.emails.root?.message}
                    </p>
                  )}
                </div>

                {/* Descripción */}
                {isViewMode ? (
                  <Textarea
                    label={t.modal.label.description}
                    value={contact?.description || '—'}
                    disabled
                    fullWidth
                    readOnly
                  />
                ) : (
                  <Textarea
                    label={t.modal.label.description}
                    placeholder={t.modal.placeholder.description}
                    {...register('description')}
                    rows={3}
                    fullWidth
                  />
                )}
              </div>
            </div>

            {/* Tab: Location */}
            <div className={activeTab === TAB_LOCATION ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
              <div className="space-y-4 overflow-y-auto flex-1 min-h-0">
                {/* Ciudad + Barrio */}
                {hasAnyGeoConfig && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {hasCities && (
                      <LookupField
                        entity={lookupConfig.cities!.entity}
                        label={t.modal.label.city}
                        displayFields={lookupConfig.cities!.displayFields}
                        value={watch('cityId') ?? null}
                        onChange={handleCityChange}
                        fetcher={lookupConfig.fetcher}
                        disabled={isViewMode}
                        readOnly={isViewMode}
                        onCreateNew={lookupConfig.cities!.onCreateNew}
                        createButtonLabel={lookupConfig.cities!.createButtonLabel}
                        createTitle={lookupConfig.cities!.createTitle}
                        createShowHeader={lookupConfig.cities!.createShowHeader}
                        createWidth={lookupConfig.cities!.createWidth}
                        createHeight={lookupConfig.cities!.createHeight}
                      />
                    )}
                    {hasNeighborhoods && (
                      <LookupField
                        entity={lookupConfig.neighborhoods!.entity}
                        label={t.modal.label.neighborhood}
                        displayFields={lookupConfig.neighborhoods!.displayFields}
                        value={watch('neighborhoodId') ?? null}
                        onChange={handleNeighborhoodChange}
                        fetcher={lookupConfig.fetcher}
                        getFilters={() => ({ CityId: watch('cityId') ?? '' })}
                        dependencies={[watch('cityId')]}
                        disabled={isViewMode || !watch('cityId')}
                        readOnly={isViewMode}
                        onCreateNew={lookupConfig.neighborhoods!.onCreateNew}
                        createButtonLabel={lookupConfig.neighborhoods!.createButtonLabel}
                        createTitle={lookupConfig.neighborhoods!.createTitle}
                        createShowHeader={lookupConfig.neighborhoods!.createShowHeader}
                        createWidth={lookupConfig.neighborhoods!.createWidth}
                        createHeight={lookupConfig.neighborhoods!.createHeight}
                      />
                    )}
                  </div>
                )}

                {/* Dirección */}
                {isViewMode ? (
                  <Input
                    label={t.modal.label.address}
                    value={contact?.address || '—'}
                    disabled
                    readOnly
                    onChange={() => {}}
                  />
                ) : (
                  <Input
                    label={t.modal.label.address}
                    placeholder={t.modal.placeholder.address}
                    {...(register('address') as any)}
                    error={!!errors.address}
                    errorMessage={errors.address?.message}
                    fullWidth
                  />
                )}
              </div>
            </div>

            {/* Tab: Phone */}
            <div className={activeTab === TAB_PHONE ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
              <div className="flex flex-col gap-4 flex-1 min-h-0">
                {(isViewMode ? (contact?.phones ?? []) : phoneFields).length === 0 ? (
                  <>
                    {!isViewMode && (
                      <div className="flex justify-end shrink-0">
                        <Button type="outline" htmlType="button" leftIcon={<Plus size={16} />} onClick={handleAddPhone}>
                          {t.modal.button.addPhone}
                        </Button>
                      </div>
                    )}
                    {/* Same blue banner as ContactGridEmptyState (the component's own
                        "No hay registros" banner) — consistent color standard across
                        every empty-state notice in ContactManager. */}
                    <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800/50 dark:bg-blue-950/30">
                      <Info className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        {t.modal.emptyState.phone}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {!isViewMode && (
                      <div className="flex justify-end shrink-0">
                        <Button type="outline" htmlType="button" leftIcon={<Plus size={16} />} onClick={handleAddPhone}>
                          {t.modal.button.addPhone}
                        </Button>
                      </div>
                    )}
                    <div className="overflow-auto flex-1 min-h-0">
                      <Table
                        columns={phoneTableColumns}
                        data={(isViewMode ? (contact?.phones ?? []) : phoneFields) as unknown as Record<string, unknown>[]}
                        showBorder={false}
                        showShadow={false}
                        showPadding={false}
                        fullWidth
                      />
                    </div>
                  </>
                )}
                {errors.phones && !Array.isArray(errors.phones) && errors.phones.message && (
                  <p className="text-xs text-red-600" role="alert">
                    {errors.phones.message}
                  </p>
                )}
              </div>
            </div>

            {/* Tab: Social Network */}
            <div className={activeTab === TAB_SOCIAL ? 'flex flex-col flex-1 min-h-0' : 'hidden'}>
              <div className="flex flex-col gap-4 flex-1 min-h-0">
                {(isViewMode ? (contact?.socialNetworks ?? []) : socialNetworkFields).length === 0 ? (
                  <>
                    {!isViewMode && (
                      <div className="flex justify-end shrink-0">
                        <Button type="outline" htmlType="button" leftIcon={<Plus size={16} />} onClick={handleAddSocialNetwork}>
                          {t.modal.button.addSocialNetwork}
                        </Button>
                      </div>
                    )}
                    {/* Same blue banner as ContactGridEmptyState — see the phone tab's
                        identical empty-state banner above for the rationale. */}
                    <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-800/50 dark:bg-blue-950/30">
                      <Info className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        {t.modal.emptyState.socialNetwork}
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    {!isViewMode && (
                      <div className="flex justify-end shrink-0">
                        <Button type="outline" htmlType="button" leftIcon={<Plus size={16} />} onClick={handleAddSocialNetwork}>
                          {t.modal.button.addSocialNetwork}
                        </Button>
                      </div>
                    )}
                    <div className="overflow-auto flex-1 min-h-0">
                      <Table
                        columns={socialTableColumns}
                        data={(isViewMode ? (contact?.socialNetworks ?? []) : socialNetworkFields) as unknown as Record<string, unknown>[]}
                        showBorder={false}
                        showShadow={false}
                        showPadding={false}
                        fullWidth
                      />
                    </div>
                  </>
                )}
                {errors.socialNetworks && !Array.isArray(errors.socialNetworks) && errors.socialNetworks.message && (
                  <p className="text-xs text-red-600" role="alert">
                    {errors.socialNetworks.message}
                  </p>
                )}
              </div>
            </div>
          </div>
          </div>
        </form>
      </DialogContent>
      {currentDefaultContactName && (
        <DefaultConfirmationDialog
          open={transferDialogOpen}
          currentDefaultName={currentDefaultContactName}
          newDefaultName={watch('name') || contact?.name || t.dialog.default.newContactFallback}
          onConfirm={handleTransferConfirm}
          onCancel={handleTransferCancel}
        />
      )}
    </Dialog>
  )
}
